import {
  Box,
  Card,
  Flex,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabProps,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  LimitedDataErrorMessage,
  MissingListPriceErrorMessage,
} from '@common/utils/exceptions';
import { FC, ReactNode, useState } from 'react';
import { ListingDisclaimer } from '~/apps/consumer/pages/Storefront/ListingDisclaimer';
import { useIsDataCoachEnabled } from '~/common/copilot/hooks/isChatEnabled';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { ListingAgentInformation } from '../../../Storefront/ListingAgent/ListingAgentCard';
import { GenericListingErrorState } from '../Errors/GenericListingErrorState';
import { LimitedDataErrorState } from '../Errors/LimitedDataErrorState';
import { MissingListPriceErrorState } from '../Errors/MissingListPriceErrorState';
import { ListingPageConfigs } from '../ListingPageConfigs';
import { useListingPageContext } from '../ListingPageContext';
import { ListingPageKeyTermsTable } from '../ListingPageKeyTerms/ListingPageKeyTermsTable';
import { METRIC_CARD_MIN_HEIGHT } from '../ListingPageMetrics/constants';
import { ListingPageMetricsGrid } from '../ListingPageMetrics/ListingPageMetricsGrid';
import { ListingChatModalWrapper } from '../ReportChat/ListingChatModalWrapper';
import { ListingCopilotSuggestions } from '../ReportChat/ListingCopilotSuggestions';
import { Comparables } from './Comparables';
import { DataSource } from './DataSource/DataSource';
import { ListingPageHeader } from './ListingPageHeader';
import { ListingPageMobileActionBar } from './Mobile/ListingPageMobileActionBar';
import { ListingPageMobileHeader } from './Mobile/ListingPageMobileHeader';
import { PropertyInfo } from './PropertyInfo';

type StyledTabProps = TabProps & {
  children: ReactNode;
};

const StyledTab: FC<StyledTabProps> = ({ children, ...props }) => (
  <Tab
    _selected={{
      color: 'white',
      fontWeight: 'medium',
      bg: 'whiteAlpha.200',
      borderColor: 'transparent',
      borderRadius: 'sm',
    }}
    fontWeight="medium"
    {...props}
  >
    {children}
  </Tab>
);

const TabbedContent: FC = () => {
  const { isLoadingReport, tables, includeIndigoData } =
    useListingPageContext();

  const listingPageTables = isLoadingReport ? (
    <Skeleton height={METRIC_CARD_MIN_HEIGHT} width="100%" />
  ) : (
    tables?.map((table) => (
      <ListingPageKeyTermsTable
        key={table.title}
        showEmptyState={!includeIndigoData}
        tableData={table}
      />
    ))
  );

  return (
    <Tabs
      isLazy
      colorScheme="purple"
      display="flex"
      flexDirection="column"
      flexGrow={1}
      variant="unstyled"
      width="100%"
    >
      <TabList>
        <StyledTab>Market trends</StyledTab>
        <StyledTab>Offer types</StyledTab>
        <StyledTab>Comparables</StyledTab>
      </TabList>
      <TabPanels flexGrow={1} overflowY="auto" pt={{ base: 0, md: 2 }} px={0}>
        <TabPanel px={0}>
          <ListingPageMetricsGrid />
        </TabPanel>
        <TabPanel px={0}>
          <VStack alignItems="stretch" spacing={4}>
            {listingPageTables}
          </VStack>
        </TabPanel>
        <TabPanel px={0}>
          <Comparables />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

// Main Component
export const ListingPageContent: FC = () => {
  const isMobile = useIsMobile();
  const { isViewOnly, reportError, listing } = useListingPageContext();
  const isDataCoachEnabled = useIsDataCoachEnabled();

  // Chat state
  const {
    isOpen: isChatOpen,
    onOpen: onChatOpen,
    onClose: onChatClose,
  } = useDisclosure();
  const [chatInitialPrompt, setChatInitialPrompt] = useState<string>();

  const handleSuggestionClick = (prompt?: string) => {
    setChatInitialPrompt(prompt);
    onChatOpen();
  };

  const mainContent = (
    <>
      {!isViewOnly && isDataCoachEnabled && (
        <Box pt={1} px={{ base: 4, md: 10 }} width="100%">
          <ListingCopilotSuggestions
            onSuggestionClick={handleSuggestionClick}
          />
        </Box>
      )}

      <Flex
        alignItems="flex-start"
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 2, md: 6 }}
        position="relative"
        px={{ base: 4, md: 10 }}
        py={2}
        width="100%"
      >
        {isMobile && <ListingPageConfigs />}

        <TabbedContent />

        {!isMobile && (
          <Box position="absolute" right={10} top={-5} width="350px">
            <ListingPageConfigs />
          </Box>
        )}
      </Flex>
      {listing && (
        <>
          <Flex
            direction="column"
            gap={4}
            pb={4}
            px={{ base: 4, md: 10 }}
            width="100%"
          >
            {isMobile && (
              <Card px={4} py={3} width="100%">
                <ListingAgentInformation listing={listing} />
              </Card>
            )}
            <ListingDisclaimer mls={listing?.mlsListing?.mls} />
          </Flex>
        </>
      )}

      {isMobile && <ListingPageMobileActionBar />}

      <ListingChatModalWrapper
        initialPrompt={chatInitialPrompt}
        isOpen={isChatOpen}
        onClose={onChatClose}
      />
    </>
  );

  const renderContent = () => {
    if (reportError) {
      return reportError.message === MissingListPriceErrorMessage ? (
        <MissingListPriceErrorState />
      ) : reportError.message === LimitedDataErrorMessage ? (
        <LimitedDataErrorState />
      ) : (
        <GenericListingErrorState />
      );
    }

    return mainContent;
  };

  return (
    <VStack
      alignItems="flex-start"
      maxWidth="1920px"
      pb={{ base: 0, md: '0' }}
      spacing={{ base: 1, md: 4 }}
      width="100%"
    >
      {isMobile ? (
        <Flex direction="column" gap={1} pt={4} px={4} width="100%">
          <Text fontSize="xl" fontWeight="medium">
            Market data for similar homes
          </Text>
          {!reportError && <DataSource />}
          <ListingPageMobileHeader />
        </Flex>
      ) : (
        <>
          <ListingPageHeader />
          <PropertyInfo />
        </>
      )}

      {renderContent()}
    </VStack>
  );
};
